import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useParams, Link as RouterLink } from 'react-router-dom';

import {
  Grid,
  Box,
  Typography,
  Button,
} from '@material-ui/core';

import {
  MultiSelect,
  Select,
  OutlinedTextField,
  OutlinedTextArea,
  Editor
} from '../common/fields';
import FilePicker from '../common/filePicker';
import Alert from '../common/alert';

import Markets from '../itemFamily/Markets';
import ActionBar from '../itemFamily/ActionBar';
import MainImage from '../itemFamily/MainImage';
import MasterCountryTooltip from '../itemFamily/MasterCountryTooltip';
import BulletList from '../itemFamily/BulletList';

import { enqueueSnackbar } from '../snackbar/snackbarSlice';
import { selectTreadGlobals } from '../app/appSlice';
import {
  selectMasterCountry,
  selectCurrentCountry,
  selectCountriesWithNames
} from '../countries/countriesSlice';
import {
  selectSaveStatus,
  clearSaveStatus,
  selectMaster,
  saveTreadDraft,
  saveTreadPublished,
  publishTread,
  selectEqualStatus
} from './treadSlice';

import { defaultStudioFolderPC24 } from '../../constants';

const defaultStudioFolder = defaultStudioFolderPC24;

const TreadPagePC24 = (props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { itemOwnerId } = useParams();

  const { status, tread, canEdit: userAllowedToEdit } = props;

  const { saveStatus, saveError } = useSelector(selectSaveStatus);
  const masterTread = useSelector(selectMaster);
  const {
    usageGroups = [],
    equipments = [],
    suitableTerrains = [],
    seasons = [],
    brands = [],
    attributes = [],
    feeds = [],
    tyrePositions = [],
  } = useSelector(selectTreadGlobals);
  const masterCountry = useSelector(selectMasterCountry);
  const currentCountry = useSelector(selectCurrentCountry);
  const allCountries = useSelector(selectCountriesWithNames);
  const equalStatus = useSelector(selectEqualStatus);

  const [ treadData, setTreadData ] = useState(tread || {});
  const [ hasUnsavedChanges, setHasUnsavedChanges ] = useState(false);
  const [ filteredEquipments, setFilteredEquipments ] = useState([]);

  const handleChange = (field, value) => {
    setTreadData({
      ...treadData,
      [field]: value
    });
    setHasUnsavedChanges(true);
  }

  const handleFieldChange = event => {
    handleChange(event.target.name, event.target.value);
  }

  const saveDraft = () => {
    // Save to server
    if(status === 'draft') {
      dispatch(saveTreadDraft({itemOwnerId, treadId: tread.code, treadData}));
    }
  };

  const saveDraftAndPublish = () => {
    // Save to server
    if(status === 'draft') {
      dispatch(publishTread({itemOwnerId, treadId: tread.code, treadData}));
    }
  }

  const savePublished = () => {
    // Save to server
    if(status === 'published') {
      dispatch(saveTreadPublished({itemOwnerId, treadId: tread.code, treadData}));
    }
  };

  const cancelChanges = () => {
    setTreadData(tread);
    setHasUnsavedChanges(false);
  };

  const renderTooltip = (content, type) => {
    if(!content || content.length === 0) {
      return null;
    }

    if(currentCountry === masterCountry) {
      return null;
    }

    return <MasterCountryTooltip content={content} type={type} masterCountry={masterCountry} />;
  }

  useEffect(() => {
    // Parent props change - basically draft vs publish change
    setTreadData(tread);
    setHasUnsavedChanges(false);
  }, [tread]);

  useEffect(() => {
    if(typeof treadData.usageGroup === 'undefined') {
      return;
    }
    // Update equipments options if usage group changes
    const newEquipmentOptions = equipments.filter(equipment => {
      return equipment.usageGroups.some(val => treadData.usageGroup.includes(val));
    });
    setFilteredEquipments(newEquipmentOptions);

    // Also remove those selected equipments that do not belong to new usage groups
    const newEquipmentOptionIds = newEquipmentOptions.map(option => option.id);
    const newEquipmentSelection = treadData.equipment.filter(equipment => {
      return newEquipmentOptionIds.includes(equipment);
    });

    setTreadData({
      ...treadData,
      equipment: newEquipmentSelection
    });

  }, [treadData.usageGroup]);

  useEffect(() => {
    // Show feedback on save status
    if(saveStatus === 'failed') {
      dispatch(enqueueSnackbar({
        message: t(`errors:${saveError.message}`),
        options: {
          variant: 'error'
        }
      }));
      dispatch(clearSaveStatus());
    } else if(saveStatus === 'succeeded') {
      dispatch(enqueueSnackbar({
        message: t('common:savedSuccessfully'),
        options: {
          variant: 'success'
        }
      }));
      dispatch(clearSaveStatus());
      setHasUnsavedChanges(false);
    }
  }, [saveStatus]);

  if(!tread.code) {
    const message = status === 'published' ? 'notPublished' : 'notFound';
    return <Alert severity="info">{t(`tread:${message}`)}</Alert>
  } else {

    return (
      <>
        <Grid container spacing={7}>
          <Grid item xs={12} md={8}>
            {status === 'draft' ? (equalStatus ? null : <Box mb={2}><Alert severity="info">{t('tread:containsUnpunblishedChanges')}</Alert></Box>) : null}

            <Box mb={2}>
              <Typography variant="subtitle1">{t('tread:tread')}</Typography>
              <Typography variant="h4" component="h1">{treadData.name}</Typography>
            </Box>

            <Box mb={2}>
              <Typography variant="overline" display="block">{t('tread:lastChanged')}</Typography>
              <Typography variant="caption">{treadData.updated}</Typography>
            </Box>

            <Box mb={4}>
              <Button variant="contained" color="primary" disableElevation component={RouterLink} to={`/pih/owner/${itemOwnerId}/tread-items?itemFamilyCode=${encodeURIComponent(tread.code)}`} target="_blank">{t('tread:showItems')}</Button>
            </Box>

            <Box my={5}>
              <MultiSelect
                label={t('tread:usageGroup')}
                name="usageGroup"
                value={treadData.usageGroup}
                options={usageGroups.map(item => ({label: item.name, value: item.id}))}
                onChange={handleFieldChange}
                disabled={!userAllowedToEdit}
              />
              <Box my={3}>
                <MultiSelect
                  label={t('tread:equipment')}
                  name="equipment"
                  value={treadData.equipment}
                  options={filteredEquipments.map(item => ({label: item.name, value: item.id}))}
                  onChange={handleFieldChange}
                  disabled={!userAllowedToEdit}
                />
              </Box>
              <Box my={3}>
                <MultiSelect
                  label={t('tread:suitableTerrains')}
                  name="suitableTerrains"
                  value={treadData.suitableTerrains}
                  options={suitableTerrains.map(item => ({label: item.name, value: item.id}))}
                  onChange={handleFieldChange}
                  disabled={!userAllowedToEdit}
                />
              </Box>
              {/*  <Box my={3}>
                <MultiSelect
                  label={t('tread:features')}
                  name="features"
                  value={treadData.features}
                  options={features.map(item => ({label: item.name, value: item.id}))}
                  onChange={handleFieldChange}
                  disabled={!userAllowedToEdit}
                />
              </Box> */}

              <Box my={3}>
                <Select
                  label={t('tread:season')}
                  name="season"
                  value={treadData.season || ''}
                  options={seasons.map(item => ({label: item.name, value: item.code}))}
                  onChange={handleFieldChange}
                  disabled={!userAllowedToEdit}
                />
              </Box>

              <Box my={3}>
                <Select
                  label={t('tread:tyrePosition')}
                  name="tyrePosition"
                  value={treadData.tyrePosition || ''}
                  options={tyrePositions.map(item => ({label: item.name, value: item.id}))}
                  onChange={handleFieldChange}
                  disabled={!userAllowedToEdit}
                />
              </Box>

              <Box my={3}>
                <Select
                  label={t('tread:brand')}
                  name="brand"
                  value={treadData.brand || ''}
                  options={brands.map(item => ({label: item.name, value: item.code}))}
                  onChange={handleFieldChange}
                  disabled={!userAllowedToEdit}
                />
              </Box>
            </Box>

            <Box my={5}>
              <Markets allCountries={allCountries} selectedCountries={treadData.markets} handleChange={newVal => handleChange('markets', newVal)} disabled={!userAllowedToEdit} />
            </Box>

            <Box my={5}>
              <Box mb={3}><Typography variant="h6" component="h2">{t('tread:feeds')}</Typography></Box>
              <MultiSelect
                label={t('tread:chooseFeeds')}
                name="feeds"
                value={treadData.feeds}
                options={feeds.map(item => ({label: item.name, value: item.id}))}
                onChange={handleFieldChange}
                disabled={!userAllowedToEdit}
              />
            </Box>

            <Box my={5}>
              <Box mb={3}><Typography variant="h6" component="h2">{t('tread:content')}</Typography></Box>

              <Box my={3}>
                <OutlinedTextField
                  label={t('tread:marketingName')}
                  name="marketingName"
                  value={treadData.marketingName}
                  onChange={handleFieldChange}
                  disabled={!userAllowedToEdit}
                  tooltip={renderTooltip(masterTread.marketingName)}
                  maxLength={50}
                />
              </Box>

              <Box my={3}>
                <OutlinedTextField
                  label={t('tread:marketingSlogan')}
                  name="marketingSlogan"
                  value={treadData.marketingSlogan}
                  onChange={handleFieldChange}
                  disabled={!userAllowedToEdit}
                  tooltip={renderTooltip(masterTread.marketingSlogan)}
                  maxLength={120}
                />
              </Box>

              <Box my={3}>

                <OutlinedTextField
                  label={t('tread:marketingTextTitle')}
                  name="marketingTextTitle"
                  value={treadData.marketingTextTitle}
                  onChange={handleFieldChange}
                  disabled={!userAllowedToEdit}
                  tooltip={renderTooltip(masterTread.marketingTextTitle)}
                  maxLength={120}
                />
              </Box>

              <Box my={3}>
                <OutlinedTextArea
                  label={t('tread:marketingTextShort')}
                  name="marketingTextShort"
                  value={treadData.marketingTextShort}
                  onChange={handleFieldChange}
                  disabled={!userAllowedToEdit}
                  tooltip={renderTooltip(masterTread.marketingTextShort)}
                  maxLength={200}
                />
              </Box>

              <Box my={3}>
                <Editor
                  label={t('tread:marketingText')}
                  name="marketingText"
                  value={treadData.marketingText}
                  onChange={handleFieldChange}
                  disabled={!userAllowedToEdit}
                  tooltip={renderTooltip(masterTread.marketingText, 'html')}
                  maxLength={500}
                />
              </Box>

              <Box my={3}>
                <OutlinedTextField
                  label={t('tread:tagline')}
                  name="tagline"
                  value={treadData.tagline}
                  onChange={handleFieldChange}
                  disabled={!userAllowedToEdit}
                  tooltip={renderTooltip(masterTread.tagline)}
                />
              </Box>

              <Box my={3}>
                <Typography variant="h6" component="h3">{t('tread:backbone')}</Typography>
                <BulletList
                  tooltip={renderTooltip(masterTread.backboneBullets, 'htmlList')}
                  data={treadData.backboneBullets || []}
                  userAllowedToEdit={userAllowedToEdit}
                  onEdit={(data) => handleChange('backboneBullets', data)}
                  addTitleText={t('tread:addBackboneText')}
                  addButtonText={t('tread:addBackboneText')}
                />
              </Box>

              <Box my={3}>
                <Typography variant="h6" component="h3">{t('tread:marketingBullets')}</Typography>
                <BulletList
                  tooltip={renderTooltip(masterTread.marketingBullets, 'htmlList')}
                  data={treadData.marketingBullets || []}
                  userAllowedToEdit={userAllowedToEdit}
                  onEdit={(data) => handleChange('marketingBullets', data)}
                />
              </Box>

            </Box>

            <Box my={5}>
              <Box mb={3}><Typography variant="h6" component="h2">{t('tread:attributes')}</Typography></Box>

              <Box my={3}>
                <MultiSelect
                  label={t('tread:chooseAttributes')}
                  name="attributes"
                  value={treadData.attributes}
                  options={attributes.attributes.map(item => ({label: item.global_name, value: item.id}))}
                  onChange={handleFieldChange}
                  disabled={!userAllowedToEdit}
                />
              </Box>

            </Box>

            <Box my={5}>
              <Box mb={3}><Typography variant="h6" component="h2">{t('tread:images')}</Typography></Box>

              {['image25degreeFull', 'imageFrontFull', 'imageSurfacePattern'].map(field =>
                <FilePicker
                  key={field}
                  fieldName={t(`tread:${field}`)}
                  fieldValue={treadData[field]}
                  setFieldValue={(file) => handleChange(field, file)}
                  disabled={!userAllowedToEdit}
                  rootFolderId={defaultStudioFolder}
                />
              )}

            </Box>

          </Grid>

          <Grid item xs={12} md={4}>
            <MainImage src={treadData.imageMain?.medium} />

            <FilePicker
              fieldName={t('tread:imageMain')}
              fieldValue={treadData.imageMain}
              setFieldValue={(file) => handleChange('imageMain', file)}
              disabled={!userAllowedToEdit}
              rootFolderId={defaultStudioFolder}
            />
          </Grid>

        </Grid>
        {hasUnsavedChanges
          ?
          <ActionBar>
            <Button variant="outlined" color="primary" onClick={cancelChanges}>{t('common:cancelChanges')}</Button>
            {
              status === 'draft' ?
                <>
                  <Button variant="contained" color="primary" onClick={saveDraft} disabled={saveStatus === 'loading'}>{t('common:saveChanges')}</Button>
                  <Button variant="contained" color="primary" onClick={saveDraftAndPublish} disabled={saveStatus === 'loading'}>{t('common:saveAndPublishChanges')}</Button>
                </>
                :
                <Button variant="contained" color="primary" onClick={savePublished} disabled={saveStatus === 'loading'}>{t('common:publishChanges')}</Button>
            }

          </ActionBar>
          :
          null
        }
      </>
    );
  }
};

TreadPagePC24.propTypes = {
  status: PropTypes.oneOf(['draft', 'published']).isRequired,
  tread: PropTypes.object,
  canEdit: PropTypes.bool.isRequired,
};

export default TreadPagePC24;
